import React from 'react';

import { KEY_COMPONENTS, MARITAL_STATUS_IDS } from '../../MainAnketa.constants';

import { FormContainer, FormSubheader, GridRowSubHeader } from '../AnketaForm.styles';
import { FileUpload } from '../FileUpload/FileUpload';

import { AddressForm } from './AddressForm/AddressForm';
import { PassportForm } from './PassportForm/PassportForm';
import { PersonalForm } from './PersonalForm/PersonalForm';
import { FILES_DESCRIPTION, PERSONAL_INFO_TITLE_EXCEPTIONS_KEYS } from './PersonalInfo.constants';
import { usePersonalInfoPresenter } from './PersonalInfo.presenter';
import * as S from './PersonalInfo.styles';
import { SecondDocumentForm } from './SecondDocumentForm/SecondDocumentForm';
import { TrusteesForm } from './TrusteesForm/TrusteesForm';

export const PersonalInfo = ({
  data,
  params,
  validate,
  errors,
  forcedValidation,
  children,
  isContentHidden = false,
  disabled = false,
  setFocusAt,
  onChange,
}) => {
  const presenter = usePersonalInfoPresenter({
    data,
    params,
    validate,
    forcedValidation,
    onChange,
  });

  const TITLE_EXCEPTIONS = {
    [PERSONAL_INFO_TITLE_EXCEPTIONS_KEYS.DRIVER_LICENSE]: (
      <span>
        Водительское
        <br />
        удостоверение
      </span>
    ),
    [PERSONAL_INFO_TITLE_EXCEPTIONS_KEYS.VEHICLE_PTS]: (
      <span>
        ПТС приобретаемого
        <br />
        автомобиля
      </span>
    ),
  };

  return (
    <FormContainer id={KEY_COMPONENTS.PERSONAL_INFO_FORM}>
      <S.Mgrid>
        <S.Title>Анкета и документы</S.Title>
      </S.Mgrid>
      <GridRowSubHeader>
        <FormSubheader id="upload">{presenter.getSubHeader}</FormSubheader>

        {presenter.isViewSelfAgreeCheckbox ? (
          <S.SelfAgreeCheckbox
            dataTest="hisApproval"
            value={presenter.getSelfAgreeValue()}
            onChange={presenter.handleSelfAgreeChange}
          >
            Клиент со своим одобрением
          </S.SelfAgreeCheckbox>
        ) : null}
      </GridRowSubHeader>
      <S.Docgrid template={presenter.docGridTemplate}>
        <S.DocsHelp>
          <p>Загрузите не менее трех разворотов паспорта:</p>
          <ul>
            <li>с фото</li>
            <li>с актуальной пропиской</li>
            <li>с отметками о ранее выданных паспортах</li>
          </ul>
          <p>И загрузите обе стороны водительского удостоверения.</p>
          <p>Если его нет, могут подойти СНИЛС, загранпаспорт, военный билет или ИНН.</p>
        </S.DocsHelp>

        {presenter.getUniqueClassesDocuments() &&
          presenter.uniqueClasses.map((uniqueClass, index) => {
            const classification =
              presenter.doctypes?.find((documentType) => {
                return documentType.sysName === uniqueClass;
              }) ?? null;

            if (!classification) {
              return null;
            }

            return (
              <FileUpload
                classification={classification}
                currentPersonId={presenter.currentPersonId}
                disabled={disabled || isContentHidden}
                doctypes={presenter.doctypes}
                gridArea={uniqueClass}
                id={`${KEY_COMPONENTS.PERSONAL_INFO_FORM}_fileupload_${uniqueClass}_${index}`}
                isContentHidden={isContentHidden}
                key={`${KEY_COMPONENTS.PERSONAL_INFO_FORM}_fileupload_${uniqueClass}_${index}`}
                title={TITLE_EXCEPTIONS[classification.value] || classification.value}
              />
            );
          })}

        <FileUpload
          currentPersonId={presenter.currentPersonId}
          disabled={disabled || isContentHidden}
          gridArea="empty_doc"
          id={`${KEY_COMPONENTS.PERSONAL_INFO_FORM}_fileupload_empty`}
          title="Загрузить документы"
          centered
          multiple
        />
        <S.FilesDescription>{FILES_DESCRIPTION}</S.FilesDescription>
      </S.Docgrid>

      {presenter.service ? (
        <S.Multidrive
          checked={presenter.insurance.common.multidrive}
          disabled={disabled}
          label="Мультидрайв"
          type="button-rounded"
          onClick={(check) => {
            return presenter.changeHandlerInsur({ multidrive: check }, 'common');
          }}
        />
      ) : null}

      {presenter.showRecognizeAttentionCard() && (
        <S.AttentionCardWrapper>
          <S.StyledAttentionCard
            dataTest="recognize-AttentionCard"
            text="Возможно, часть документов распознана неправильно. Проверьте данные ещё раз"
            hasCloseCross
            onClick={() => {
              presenter.setShowAttention(false);
              presenter.setShowAttentionFirstTime(false);
            }}
          />
        </S.AttentionCardWrapper>
      )}

      <GridRowSubHeader>
        <FormSubheader id="personal-info">Личная информация</FormSubheader>
      </GridRowSubHeader>
      <PersonalForm
        clearConfidenceErrors={presenter.clearPassportConfidenceErrors}
        confidenceErrors={presenter.passportConfidenceErrors}
        disabled={disabled}
        errors={errors}
        isContentHidden={isContentHidden}
        persona={presenter.persons[presenter.currentPersonId]}
        setFocusAt={setFocusAt}
        validate={presenter.getPersonalFormValidate()}
        onChange={presenter.changeHandlerPersons}
      />
      <S.Anchor ref={presenter.refs.passport} />
      <PassportForm
        clearConfidenceErrors={presenter.clearPassportConfidenceErrors}
        confidenceErrors={presenter.passportConfidenceErrors}
        disabled={disabled}
        errors={errors}
        isContentHidden={isContentHidden}
        persona={presenter.persons[presenter.currentPersonId]}
        setFocusAt={setFocusAt}
        validate={presenter.getPassportFormValidate()}
        onChange={presenter.changeHandlerPersons}
      />
      <SecondDocumentForm
        disabled={disabled}
        errors={errors}
        isContentHidden={isContentHidden}
        persona={presenter.persons[presenter.currentPersonId]}
        validate={presenter.getSecondDocFormValidate()}
        onChange={presenter.changeHandlerPersons}
      />
      <AddressForm
        clearConfidenceErrors={presenter.clearPassportConfidenceErrors}
        confidenceErrors={presenter.passportConfidenceErrors}
        disabled={disabled}
        errors={errors}
        persona={presenter.persons[presenter.currentPersonId]}
        setFocusAt={setFocusAt}
        validate={presenter.getAddressFormValidate()}
        onChange={presenter.changeHandlerPersons}
      />
      <TrusteesForm
        disabled={disabled}
        errors={errors}
        isContentHidden={isContentHidden}
        setFocusAt={setFocusAt}
        spouseForm={presenter.personalInfo?.familyState?.id === MARITAL_STATUS_IDS.MARRIED_PERSON}
        validate={presenter.getTrusteesFormValidate()}
        onChange={presenter.changeTrustHandler}
        onChangeSpouse={presenter.changeHandlerPersons}
      />
      {children}
    </FormContainer>
  );
};
